import {useEffect, useRef, useState} from "react";
import {Image as ImageIcon, Youtube, Send, Heart, Trash2, Loader2, X, Users, Flag, MessageCircle} from "lucide-react";
import {apiGet, apiPost, apiDelete, apiUpload} from "@/lib/api";

const SCOPES = [["following", "Following"], ["all", "Discover"], ["mine", "My posts"]];
const YT_RX = /(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/;
const ago = (iso) => {
  const m = Math.max(1, Math.round((Date.now() - new Date(iso)) / 6e4));
  return m < 60 ? `${m}m` : m < 1440 ? `${Math.round(m / 60)}h` : `${Math.round(m / 1440)}d`;
};
const initials = (n = "") => n.split(" ").map(x => x[0]).join("").slice(0, 2).toUpperCase();

function Composer({user, communities, onPosted, notify}) {
  const [text, setText] = useState("");
  const [images, setImages] = useState([]);
  const [yt, setYt] = useState("");
  const [showYt, setShowYt] = useState(false);
  const [samaj, setSamaj] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const mine = communities.filter(c => c.my_status === "member");
  const ytId = yt.match(YT_RX)?.[1];

  const addFiles = async (files) => {
    setBusy(true);
    try {
      for (const f of Array.from(files).slice(0, 4 - images.length)) {
        if (f.size > 5 * 1024 * 1024) { notify(`${f.name} is larger than 5MB`); continue; }
        const r = await apiUpload(f);
        setImages(p => [...p, r.url].slice(0, 4));
      }
    } catch { notify("Could not upload that photo"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (yt && !ytId) { notify("Please paste a valid YouTube link"); return; }
    if (!text.trim() && !images.length && !ytId) { notify("Write something, add a photo or a YouTube link"); return; }
    setBusy(true);
    try {
      const p = await apiPost("/posts", {text, images, youtube_url: yt, community_id: samaj});
      onPosted(p); setText(""); setImages([]); setYt(""); setShowYt(false); setSamaj("");
      notify("Posted to your feed");
    } catch (err) { notify(err?.response?.data?.detail || "Could not post"); }
    finally { setBusy(false); }
  };

  return (
    <form className="feed-composer" onSubmit={submit} data-testid="feed-composer">
      <div className="fc-avatar">{user.picture ? <img src={user.picture} alt=""/> : initials(user.name)}</div>
      <div className="fc-main">
        <textarea data-testid="feed-text" maxLength={1000} value={text} onChange={e => setText(e.target.value)} placeholder="Shu chale che? Share a thought, a photo or a video..."/>
        {images.length > 0 && (
          <div className="fc-thumbs">
            {images.map((src, i) => (
              <div key={src} className="fc-thumb"><img src={src} alt=""/><button type="button" data-testid={`feed-remove-image-${i}`} onClick={() => setImages(images.filter(x => x !== src))}><X size={11}/></button></div>
            ))}
          </div>
        )}
        {showYt && <input data-testid="feed-youtube" className="fc-yt" value={yt} onChange={e => setYt(e.target.value)} placeholder="Paste a YouTube link"/>}
        {ytId && <div className="fc-yt-preview"><img src={`https://i.ytimg.com/vi/${ytId}/hqdefault.jpg`} alt="YouTube preview"/></div>}
        <div className="fc-tools">
          <input ref={fileRef} type="file" accept="image/*" multiple hidden data-testid="feed-image-input" onChange={e => addFiles(e.target.files)}/>
          <button type="button" data-testid="feed-add-image" disabled={busy || images.length >= 4} onClick={() => fileRef.current?.click()}><ImageIcon size={16}/> Photo</button>
          <button type="button" data-testid="feed-add-youtube" className={showYt ? "on" : ""} onClick={() => setShowYt(!showYt)}><Youtube size={16}/> YouTube</button>
          {mine.length > 0 && (
            <select data-testid="feed-samaj" value={samaj} onChange={e => setSamaj(e.target.value)}>
              <option value="">My followers</option>
              {mine.map(c => <option key={c.community_id} value={c.community_id}>{c.name}</option>)}
            </select>
          )}
          <button data-testid="feed-submit" className="coral-button sm" type="submit" disabled={busy}>{busy ? <Loader2 size={14} className="spin"/> : <Send size={14}/>} Post</button>
        </div>
      </div>
    </form>
  );
}

function Comments({p, user, openMember, notify, onCount}) {
  const [list, setList] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { apiGet(`/posts/${p.post_id}/comments`).then(setList).catch(() => setList([])); }, [p.post_id]);
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || busy) return;
    setBusy(true);
    try { const c = await apiPost(`/posts/${p.post_id}/comments`, {text}); setList(l => [...l, c]); setText(""); onCount(1); }
    catch (err) { notify(err?.response?.data?.detail || "Could not reply"); }
    finally { setBusy(false); }
  };
  const like = async (c) => {
    try { const r = c.liked ? await apiDelete(`/comments/${c.comment_id}/like`) : await apiPost(`/comments/${c.comment_id}/like`, {}); setList(l => l.map(x => x.comment_id === c.comment_id ? {...x, ...r} : x)); }
    catch { notify("Please try again"); }
  };
  const del = async (id) => { try { await apiDelete(`/comments/${id}`); setList(l => l.filter(c => c.comment_id !== id)); onCount(-1); } catch { notify("Could not remove"); } };
  if (!list) return <div className="tweet-comments"><Loader2 size={14} className="spin"/></div>;
  return (
    <div className="tweet-comments" data-testid={`feed-comments-${p.post_id}`}>
      {list.map(c => (
        <div key={c.comment_id} className="tc-row" data-testid={`comment-${c.comment_id}`}>
          <button className="fc-avatar sm" onClick={() => openMember(c.user_id)}>{c.author_picture ? <img src={c.author_picture} alt=""/> : initials(c.author)}</button>
          <div className="tc-bubble">
            <button className="author-link" onClick={() => openMember(c.user_id)}>{c.author}</button> <small>· {ago(c.created_at)}</small>
            <p>{c.text}</p>
            <button data-testid={`comment-like-${c.comment_id}`} className={c.liked ? "tc-like on" : "tc-like"} onClick={() => like(c)}><Heart size={11} fill={c.liked ? "currentColor" : "none"}/> {c.likes || 0}</button>
          </div>
          {(c.user_id === user.user_id || p.user_id === user.user_id || user.is_admin) && <button className="tc-del" data-testid={`comment-delete-${c.comment_id}`} onClick={() => del(c.comment_id)}><Trash2 size={12}/></button>}
        </div>
      ))}
      {p.can_comment ? (
        <form className="tc-form" onSubmit={send}>
          <input data-testid={`comment-input-${p.post_id}`} maxLength={600} value={text} onChange={e => setText(e.target.value)} placeholder="Write a reply..."/>
          <button data-testid={`comment-send-${p.post_id}`} className="coral-button sm" type="submit" disabled={busy || !text.trim()}><Send size={13}/></button>
        </form>
      ) : <small className="tc-locked" data-testid={`comment-locked-${p.post_id}`}>Follow {p.author.split(" ")[0]} or join a samaj with them to reply.</small>}
    </div>
  );
}

function Post({p, user, openMember, onChange, notify}) {
  const [open, setOpen] = useState(false);
  const like = async () => {
    try { const r = p.liked ? await apiDelete(`/posts/${p.post_id}/like`) : await apiPost(`/posts/${p.post_id}/like`, {}); onChange({...p, ...r}); }
    catch { notify("Please try again"); }
  };
  const remove = async () => { try { await apiDelete(`/posts/${p.post_id}`); onChange(null); notify("Post removed"); } catch { notify("Could not remove"); } };
  const report = () => apiPost("/reports", {content_type: "post", content_id: p.post_id, reason: "Flagged from feed"}).then(() => notify("Aabhar — report received")).catch(() => notify("Please try again"));
  const own = p.user_id === user.user_id;
  return (
    <article className="tweet" data-testid={`feed-post-${p.post_id}`}>
      <button className="fc-avatar" onClick={() => openMember(p.user_id)}>{p.author_picture ? <img src={p.author_picture} alt=""/> : initials(p.author)}</button>
      <div className="tweet-main">
        <div className="tweet-head">
          <button className="author-link" data-testid={`feed-author-${p.post_id}`} onClick={() => openMember(p.user_id)}>{p.author}</button>
          {p.samaj_name && <span className="tweet-samaj"><Users size={11}/> {p.samaj_name}</span>}
          <small>· {ago(p.created_at)}</small>
        </div>
        {p.text && <p className="tweet-text">{p.text}</p>}
        {p.images?.length > 0 && <div className={`tweet-images n${p.images.length}`}>{p.images.map(src => <img key={src} src={src} alt="" loading="lazy"/>)}</div>}
        {p.youtube_id && (
          <div className="tweet-video" data-testid={`feed-video-${p.post_id}`}>
            <iframe src={`https://www.youtube-nocookie.com/embed/${p.youtube_id}`} title="YouTube video" loading="lazy" allow="accelerometer; encrypted-media; gyroscope; picture-in-picture" allowFullScreen/>
          </div>
        )}
        <div className="tweet-actions">
          <button data-testid={`feed-like-${p.post_id}`} className={p.liked ? "liked" : ""} onClick={like}><Heart size={15} fill={p.liked ? "currentColor" : "none"}/> {p.likes || 0}</button>
          <button data-testid={`feed-comment-toggle-${p.post_id}`} className={open ? "liked" : ""} onClick={() => setOpen(!open)}><MessageCircle size={15}/> {p.comments || 0}</button>
          {own || user.is_admin
            ? <button data-testid={`feed-delete-${p.post_id}`} onClick={remove}><Trash2 size={14}/> Remove</button>
            : <button data-testid={`feed-report-${p.post_id}`} onClick={report}><Flag size={14}/> Report</button>}
        </div>
        {open && <Comments p={p} user={user} openMember={openMember} notify={notify} onCount={(k) => onChange({...p, comments: Math.max(0, (p.comments || 0) + k)})}/>}
      </div>
    </article>
  );
}

export default function Feed({user, communities, openMember, notify, setActive}) {
  const [scope, setScope] = useState("following");
  const [posts, setPosts] = useState(null);
  useEffect(() => { setPosts(null); apiGet("/feed", {scope}).then(setPosts).catch(() => setPosts([])); }, [scope]);
  const update = (id, next) => setPosts(ps => next ? ps.map(x => x.post_id === id ? next : x) : ps.filter(x => x.post_id !== id));

  return (
    <div className="feed-page" data-testid="feed-page">
      <div className="page-title">
        <div>
          <div className="eyebrow">MY FEED · TAMARU</div>
          <h1>What your people <em>are sharing.</em></h1>
          <p>Posts, photos and YouTube videos from the members you follow and the samaj you belong to.</p>
        </div>
      </div>
      <div className="feed-col">
        <Composer user={user} communities={communities} notify={notify} onPosted={p => setPosts(ps => [p, ...(ps || [])])}/>
        <div className="activity-tabs" data-testid="feed-scopes">
          {SCOPES.map(([id, l]) => <button key={id} data-testid={`feed-scope-${id}`} className={scope === id ? "activity-tab active" : "activity-tab"} onClick={() => setScope(id)}>{l}</button>)}
        </div>
        {posts === null ? <div className="empty-pane"><Loader2 className="spin"/></div> : posts.length === 0 ? (
          <div className="empty-pane" data-testid="feed-empty">
            <h3>{scope === "following" ? "Your feed is quiet" : "No posts yet"}</h3>
            <p>{scope === "following" ? "Follow members or join a samaj to see their posts here." : "Be the first to share something."}</p>
            {scope === "following" && <div className="feed-empty-actions"><button data-testid="feed-discover" className="coral-button sm" onClick={() => setScope("all")}>Discover posts</button><button data-testid="feed-find-samaj" className="outline-button sm" onClick={() => setActive("samaj")}>Find a samaj</button></div>}
          </div>
        ) : posts.map(p => <Post key={p.post_id} p={p} user={user} openMember={openMember} notify={notify} onChange={(n) => update(p.post_id, n)}/>)}
      </div>
    </div>
  );
}
